import { Tile, isSuited, isHonor, isTerminalOrHonor, tileNumber, NUM_TILE_TYPES } from '../tiles';
import { TileCounts } from '../hand';
import { Group, StandardParse, decomposeStandard } from '../decompose';
import { isSevenPairs, isKokushi } from '../agari';

export interface WinContext {
  winningTile: Tile;
  isTsumo: boolean;
  isRiichi: boolean;
  seatWind: Tile;  // 27=E 28=S 29=W 30=N
  roundWind: Tile;
}

export interface YakuResult { key: string; name: string; han: number }
export interface HandYaku {
  yaku: YakuResult[];
  han: number;
  yakuman: boolean;
  parse: StandardParse | null;
}

const DRAGONS = [31, 32, 33]; // 백 발 중
const DRAGON_KEY: Record<number, string> = { 31: 'haku', 32: 'hatsu', 33: 'chun' };

function suitOf(t: Tile): number { return isHonor(t) ? 3 : Math.floor(t / 9); } // 0man 1pin 2sou 3honor

// shuntsu의 세 타일
function seqTiles(g: Group): Tile[] { return [g.tile, g.tile + 1, g.tile + 2]; }

/** 표준형 분해 하나에 대한 역 목록 (門前). 도라 제외. */
function yakuForParse(parse: StandardParse, counts: TileCounts, ctx: WinContext): YakuResult[] {
  const out: YakuResult[] = [];
  const melds = parse.groups.filter((g) => g.type !== 'pair');
  const pair = parse.groups.find((g) => g.type === 'pair')!;
  const seqs = melds.filter((g) => g.type === 'shuntsu');
  const trips = melds.filter((g) => g.type === 'koutsu');
  const allTiles: Tile[] = [];
  for (let t = 0; t < NUM_TILE_TYPES; t++) for (let i = 0; i < counts[t]; i++) allTiles.push(t);

  const add = (key: string, name: string, han: number) => out.push({ key, name, han });

  // 리치 / 멘젠쯔모 (門前 전제)
  if (ctx.isRiichi) add('riichi', '리치', 1);
  if (ctx.isTsumo) add('menzen_tsumo', '멘젠쯔모', 1);

  // 탕야오: 요구패 없음
  if (allTiles.every((t) => !isTerminalOrHonor(t))) add('tanyao', '탕야오', 1);

  // 역패: 삼원패 커쯔 / 장풍 / 자풍
  for (const g of trips) {
    if (DRAGONS.includes(g.tile)) add(`yakuhai_${DRAGON_KEY[g.tile]}`, '역패', 1);
    if (g.tile === ctx.seatWind) add('yakuhai_seat', '자풍', 1);
    if (g.tile === ctx.roundWind) add('yakuhai_round', '장풍', 1);
  }

  // 핑후: 4슌쯔 + 비역패 작두 + 양면대기
  const pairIsYakuhai =
    DRAGONS.includes(pair.tile) || pair.tile === ctx.seatWind || pair.tile === ctx.roundWind;
  if (seqs.length === 4 && !pairIsYakuhai) {
    // 양면대기: 화료패가 어떤 슌쯔의 양끝(가장 낮은/높은)이며 변짱/칸짱이 아님
    const w = ctx.winningTile;
    const ryanmen = seqs.some((g) => {
      const [a, , c2] = seqTiles(g);
      // 화료패가 a(낮은쪽)이고 그 슌쯔가 7..9를 막지 않음(=a의 숫자!=7) -> 양면, 혹은 화료패가 c2(높은쪽)이고 a의 숫자!=1
      if (w === a && tileNumber(a) !== 7) return true;     // a,a+1 + (a+2 대기)였던 양면
      if (w === c2 && tileNumber(a) !== 1) return true;    // (a 대기) a+1,a+2 양면
      return false;
    });
    if (ryanmen) add('pinfu', '핑후', 1);
  }

  // 일배구 / 이배구: 동일 슌쯔 쌍 개수
  const seqKey = (g: Group) => g.tile;
  const seqCounts = new Map<number, number>();
  for (const g of seqs) seqCounts.set(seqKey(g), (seqCounts.get(seqKey(g)) ?? 0) + 1);
  let identicalPairs = 0;
  for (const n of seqCounts.values()) identicalPairs += Math.floor(n / 2);
  if (identicalPairs === 2) add('ryanpeikou', '이배구', 3);
  else if (identicalPairs === 1) add('iipeikou', '일배구', 1);

  // 삼색동순: 같은 숫자 시작 슌쯔가 세 수패에 존재
  for (let n = 0; n <= 6; n++) {
    const haveM = seqs.some((g) => g.tile === n);
    const haveP = seqs.some((g) => g.tile === n + 9);
    const haveS = seqs.some((g) => g.tile === n + 18);
    if (haveM && haveP && haveS) { add('sanshoku', '삼색동순', 2); break; }
  }

  // 삼색동각: 같은 숫자 커쯔가 세 수패
  for (let n = 0; n <= 8; n++) {
    const haveM = trips.some((g) => g.tile === n);
    const haveP = trips.some((g) => g.tile === n + 9);
    const haveS = trips.some((g) => g.tile === n + 18);
    if (haveM && haveP && haveS) { add('sanshoku_doukou', '삼색동각', 2); break; }
  }

  // 일기통관: 한 수패에 123,456,789
  for (let suit = 0; suit < 3; suit++) {
    const base = suit * 9;
    if (seqs.some((g) => g.tile === base) && seqs.some((g) => g.tile === base + 3) && seqs.some((g) => g.tile === base + 6)) {
      add('ittsuu', '일기통관', 2); break;
    }
  }

  // 토이토이: 4커쯔
  if (trips.length === 4) add('toitoi', '토이토이', 2);

  // 삼암각: 커쯔 3개 이상(門前 전제이므로 모두 암각, 단 론으로 완성된 커쯔는 명각 취급)
  // 화료패가 커쯔를 완성했고 론이면 그 커쯔는 암각 아님
  let ankou = 0;
  for (const g of trips) {
    const isCompletedByRon = !ctx.isTsumo && g.tile === ctx.winningTile;
    if (!isCompletedByRon) ankou++;
  }
  if (ankou === 4) add('suuankou', '사암각', 13); // 역만
  else if (ankou === 3) add('sanankou', '삼암각', 2);

  // 챤타/준챤: 모든 면자+작두가 요구패(끝수/자패) 포함
  const groupHasTerminalOrHonor = (g: Group): boolean => {
    if (g.type === 'shuntsu') return seqTiles(g).some(isTerminalOrHonor);
    return isTerminalOrHonor(g.tile);
  };
  const groupHasHonor = (g: Group): boolean => {
    if (g.type === 'shuntsu') return seqTiles(g).some(isHonor);
    return isHonor(g.tile);
  };
  // 혼노두(전부 요구패)는 챤타/준챤과 중복되지 않음 → honroutou 손패는 제외
  if (parse.groups.every(groupHasTerminalOrHonor) && !allTiles.every(isTerminalOrHonor)) {
    const anyHonor = parse.groups.some(groupHasHonor);
    if (anyHonor) add('chanta', '챤타', 2);
    else add('junchan', '준챤', 3);
  }

  // 혼노두: 모든 패가 요구패 (커쯔/작두만, 슌쯔 불가)
  if (allTiles.every(isTerminalOrHonor)) add('honroutou', '혼노두', 2);

  // 소삼원 / 대삼원
  const dragonTrips = trips.filter((g) => DRAGONS.includes(g.tile)).length;
  const dragonPair = DRAGONS.includes(pair.tile);
  if (dragonTrips === 3) add('daisangen', '대삼원', 13); // 역만
  else if (dragonTrips === 2 && dragonPair) add('shousangen', '소삼원', 2);

  // 혼일색 / 청일색: 수패가 한 종류
  const suitsUsed = new Set(allTiles.filter(isSuited).map(suitOf));
  const hasHonor = allTiles.some(isHonor);
  if (suitsUsed.size === 1) {
    if (!hasHonor) add('chinitsu', '청일색', 6);
    else add('honitsu', '혼일색', 3);
  }

  return out;
}

/** 칠대자 역. */
function yakuForChiitoitsu(counts: TileCounts, ctx: WinContext): YakuResult[] {
  const out: YakuResult[] = [];
  if (ctx.isRiichi) out.push({ key: 'riichi', name: '리치', han: 1 });
  if (ctx.isTsumo) out.push({ key: 'menzen_tsumo', name: '멘젠쯔모', han: 1 });
  out.push({ key: 'chiitoitsu', name: '칠대자', han: 2 });
  const tiles: Tile[] = [];
  for (let t = 0; t < NUM_TILE_TYPES; t++) for (let i = 0; i < counts[t]; i++) tiles.push(t);
  if (tiles.every((t) => !isTerminalOrHonor(t))) out.push({ key: 'tanyao', name: '탕야오', han: 1 });
  const suitsUsed = new Set(tiles.filter(isSuited).map(suitOf));
  const hasHonor = tiles.some(isHonor);
  if (suitsUsed.size === 1) {
    if (!hasHonor) out.push({ key: 'chinitsu', name: '청일색', han: 6 });
    else out.push({ key: 'honitsu', name: '혼일색', han: 3 });
  }
  if (tiles.every(isTerminalOrHonor)) out.push({ key: 'honroutou', name: '혼노두', han: 2 });
  return out;
}

function sumHan(yaku: YakuResult[]): number {
  return yaku.reduce((s, y) => s + y.han, 0);
}
function hasYakuman(yaku: YakuResult[]): boolean {
  return yaku.some((y) => y.han >= 13);
}

/** 표준/칠대자/국사 전부에 대한 모든 후보 해석을 반환한다(役 유무 무관). win.ts가 점수로 최적 선택. */
export function detectYakuCandidates(counts: TileCounts, ctx: WinContext): HandYaku[] {
  const candidates: HandYaku[] = [];

  if (isKokushi(counts)) {
    candidates.push({ yaku: [{ key: 'kokushi', name: '국사무쌍', han: 13 }], han: 13, yakuman: true, parse: null });
  }
  if (isSevenPairs(counts)) {
    const yaku = yakuForChiitoitsu(counts, ctx);
    candidates.push({ yaku, han: sumHan(yaku), yakuman: false, parse: null });
  }
  for (const parse of decomposeStandard(counts)) {
    const yaku = yakuForParse(parse, counts, ctx);
    candidates.push({ yaku, han: sumHan(yaku), yakuman: hasYakuman(yaku), parse });
  }
  return candidates;
}

/** 표준/칠대자/국사 전부 고려해 가장 높은 판수의 해석을 반환(역만 우선). 표시·판정용. */
export function detectYaku(counts: TileCounts, ctx: WinContext): HandYaku {
  const candidates = detectYakuCandidates(counts, ctx);
  if (candidates.length === 0) return { yaku: [], han: 0, yakuman: false, parse: null };
  candidates.sort((a, b) => {
    if (a.yakuman !== b.yakuman) return a.yakuman ? -1 : 1;
    return b.han - a.han;
  });
  return candidates[0];
}
